import { useEffect, useState } from "react";

import useWebSocket, { ReadyState } from "react-use-websocket";
import { useSnackbar } from "notistack";
import { Agent, ItemType, Message, WSMessage } from "../Components/interface";
import { tokenStorage } from "../Auth/storage";
// const SOCKET_URL = "wss://ai.beralink.com/ws/nft";
const SOCKET_URL =
  process.env.REACT_APP_WS_API_URL || "wss://ai.beralink.com/ws/nft";

export function useAIChat() {
  // const [messages, setMessages] = useState<Message[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const AgentList: Agent[] = [
    {
      id: 1,
      type: "market",
      title: "AI Market Agent",
      collapsed: false,
      completed: false,
      streaming: false,
      thinkingBuffer: "",
      messages: [],
    },
    {
      id: 2,
      type: "risk",
      title: "AI Risk Agent",
      collapsed: false,
      completed: false,
      streaming: false,
      thinkingBuffer: "",
      messages: [],
    },
    {
      id: 3,
      type: "whale",
      title: "AI Whale Agent",
      collapsed: false,
      completed: false,
      streaming: false,
      thinkingBuffer: "",
      messages: [],
    },
  ];

  const [multiAgents, setMultiAgents] = useState<Agent[]>([
    {
      id: 1,
      type: "market",
      title: "AI Market Agent",
      collapsed: false,
      completed: false,
      streaming: false,
      thinkingBuffer: "",
      messages: [],
    },
  ]);

  const [fullResponse, setFullResponse] = useState("");
  const [connect, setConnect] = useState(false);
  const { readyState, sendJsonMessage, lastMessage, getWebSocket } =
    useWebSocket(
      connect ? `${SOCKET_URL}?token=${tokenStorage.get()}` : null,
      {
        shouldReconnect: () => true,
        reconnectAttempts: 1,
        reconnectInterval: 3000,
        onOpen: () => {
          console.log("WS connected");
        },
        onError: (e) => {
          console.error("WS error", e);
          enqueueSnackbar("WebSocket connection error. Please try again.", {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        },
      },
      connect,
    );

  // websocket listener
  useEffect(() => {
    if (!lastMessage) return;

    const data: WSMessage = JSON.parse(lastMessage.data);

    // token stream
    if (data.type === "token") {
      const token = data.content || "";

      setFullResponse((prev) => prev + token);

      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                streaming: true,
                thinkingBuffer: agent.thinkingBuffer + token,
              }
            : agent,
        ),
      );
    }

    // stream done
    if (data.type === "report") {
      const report = data.content || "";
      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                streaming: false,
                thinkingBuffer: "",
                subtitle: data.tokens + "[@@@]" + data.times + "S"
              }
            : agent,
        ),
      );

      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                messages: [
                  ...agent.messages,
                  {
                    role: "assistant",
                    type: agent.type,
                    title: data?.content ? data.content : "AI Response",
                    content: report,
                    collapsed: false,
                    completed: true,
                  },
                ],
              }
            : agent,
        ),
      );
    }

    // stream done
    if (data.type === "done") {
      setFullResponse("");

      if (data.content === "skipped") {
        setMultiAgents((prev) =>
          prev.map((agent) =>
            agent.id === data.id
              ? {
                  ...agent,
                  messages: [
                    {
                      role: "feedback",
                      type: agent.type,
                      title: data?.content ? data.content : "AI Response",
                      content: agent.title + " analysis was skipped",
                      collapsed: true,
                      completed: true,
                    },
                  ],
                }
              : agent,
          ),
        );
      }

      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                completed: true,
              }
            : agent,
        ),
      );

      if (data.id < AgentList.length) {
        createAgent(data.id);
      }
    }

    // awaiting
    if (data.type === "awaiting") {
      console.log("Awaiting user feedback for agent", data, multiAgents);
      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                streaming: false,
                thinkingBuffer: "",
              }
            : agent,
        ),
      );

      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                messages: [
                  ...agent.messages,
                  {
                    role: "awaiting",
                    type: agent.type,
                    content: data.content || "",
                    title: "AI Market Agent",
                    collapsed: false,
                    completed: false,
                  },
                ],
              }
            : agent,
        ),
      );
    }

    // feedback
    if (data.type === "feedback") {
      setMultiAgents((prev) =>
        prev.map((agent) =>
          agent.id === data.id
            ? {
                ...agent,
                messages: agent.messages.filter(
                  (message) =>
                    message.role !== "awaiting" && message.role !== "user",
                ),
              }
            : agent,
        ),
      );
    }
  }, [lastMessage]);

  // send message
  const sendMessage = (item: ItemType) => {
    console.log("INPUT", item);
    if (!item) return;

    // setMessages((prev) => [
    //   ...prev,
    //   {
    //     role: "user",
    //     content: msg,
    //     title: 'User Input',
    //     collapsed: true,
    //     completed: true,
    //   },
    // ]);

    // setThinkingBuffer("");

    setFullResponse("");

    sendJsonMessage({
      ...item,
      message: item.name,
    });
  };

  // approve
  const approve = (type: string) => {
    multiAgents.map((agent) => {
      if (agent.type !== type) {
        agent.collapsed = true;
      }
    });

    sendJsonMessage({
      message: "approve",
    });
  };

  // reject
  const reject = (type: string) => {
    multiAgents.map((agent) => {
      if (agent.type !== type) {
        agent.collapsed = true;
      }
    });

    sendJsonMessage({
      message: "reject",
    });
  };

  // collapse toggle
  const toggleCollapse = (index: number) => {
    const isCollappsed = multiAgents.find(
      (agent) => agent.id === index,
    )?.collapsed;
    setMultiAgents((prev) =>
      prev.map((agent) =>
        agent.id === index
          ? {
              ...agent,
              collapsed: !agent.collapsed,
            }
          : {
              ...agent,
              collapsed: isCollappsed ? true : agent.collapsed,
            },
      ),
    );
  };

  const handleConnect = () => {
    if (readyState === ReadyState.OPEN) {
      return;
    }
    setConnect(true);
  };

  const disconnect = () => {
    setConnect(false);
    const ws = getWebSocket();
    if (ws) {
      ws.close(1000, "manual disconnect");
      console.log("WebSocket manually disconnected");
    }
  };

  const resetMultiAgents = () => {
    setMultiAgents([
      {
        id: 1,
        type: "market",
        title: "AI Market Agent",
        collapsed: false,
        completed: false,
        streaming: false,
        thinkingBuffer: "",
        messages: [],
      },
    ]);
  };

  const cleanMarkdownAndNewlines = (text: string) => {
    return text
      .replace(/(\*\*|__)(.*?)\1/g, "$2") // 过滤加粗 **text** -> text
      .replace(/(\*|_)(.*?)\1/g, "$2") // 过滤斜体 *text* -> text
      .replace(/`([^`]+)`/g, "$1") // 过滤行内代码 `code` -> code
      .replace(/#+\s+.*/g, "") // 过滤 Markdown 标题 (H1, H2 等整个标题行)
      .replace(/[-*+]\s+/g, "") // 过滤无序列表符号
      .replace(/\d+\.\s+/g, "") // 过滤有序列表数字 (1. 2. 3.)
      .replace(/[\n\r]+/g, " ") // 👑 过滤所有换行符，全部变成空格（如果是 Checklist 场景很实用）
      .trim();
  };

  const createAgent = (index: number) => {
    setMultiAgents((prev) => [
      ...prev,
      AgentList.find((agent) => agent.id === index + 1) || {
        id: index + 1,
        type: "market",
        title: `AI Agent ${index + 1}`,
        collapsed: false,
        completed: false,
        streaming: false,
        thinkingBuffer: "",
        messages: [],
      },
    ]);
  };

  return {
    multiAgents,
    handleConnect,
    disconnect,
    sendMessage,
    approve,
    reject,
    toggleCollapse,
    resetMultiAgents,
  };
}
